import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Download, Loader2 } from "lucide-react";

export const PAYROLL_QUICK_REPORT_ITEMS = [
  { key: "projection", label: "Payroll Projection", screen: "pay_rpt_projection" },
  { key: "register", label: "Payroll Register", screen: "pay_rpt_register" },
  { key: "reconciliation", label: "Payroll Reconciliation", screen: "pay_rpt_reconciliation" },
  { key: "journal", label: "Accounting Journal", screen: "pay_rpt_journal" },
  { key: "unpaid", label: "Unpaid Salary", screen: "pay_rpt_unpaid" },
  { key: "le_away", label: "Leave Encashment Away", screen: "pay_rpt_leave_encashment_away" },
];

/**
 * Dropdown: Quick Reports ▾
 * Menu is portaled to document.body so header overflow / refresh icon cannot cover it.
 * Pass `allowedKeys` to filter by RBAC (omit / empty = show none; null = show all).
 */
export default function PayrollReportsMenu({
  darkMode,
  disabled = false,
  exporting = false,
  onSelect,
  allowedKeys = null,
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const rootRef = useRef(null);
  const menuRef = useRef(null);

  const items = useMemo(() => {
    if (allowedKeys == null) return PAYROLL_QUICK_REPORT_ITEMS;
    const set = new Set(allowedKeys);
    return PAYROLL_QUICK_REPORT_ITEMS.filter((item) => set.has(item.key));
  }, [allowedKeys]);

  const updateMenuPos = () => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPos({
      top: Math.round(rect.bottom + 4),
      right: Math.round(window.innerWidth - rect.right),
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPos();
    const onWin = () => updateMenuPos();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!items.length) return null;

  const btnClass = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:border-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))]"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-sm hover:border-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))]";
  const menuClass = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";

  return (
    <div className="relative z-50" ref={rootRef}>
      <button
        type="button"
        disabled={disabled || exporting}
        onClick={() => setOpen((v) => !v)}
        title="Quick Reports"
        className={`inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${btnClass}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        Quick Reports
        <ChevronDown size={14} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: menuPos.top, right: menuPos.right }}
            className={`fixed z-[100080] w-56 overflow-hidden rounded-lg border shadow-lg ${menuClass}`}
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className={`w-full border-b px-3 py-2 text-left text-xs font-semibold last:border-b-0 ${
                  darkMode
                    ? "border-[#333] hover:bg-[hsl(var(--ds-muted))]"
                    : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                }`}
                onClick={() => {
                  setOpen(false);
                  onSelect?.(item.key);
                }}
              >
                {item.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
